
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { CreditCard, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatDate } from "@/lib/utils";

interface PaymentMethod {
  brand: string;
  last4: string;
  expMonth?: number;
  expYear?: number;
}

interface SubscriptionDetailsProps {
  userRole: string;
  nextBilling: string | null;
  isLoading: boolean;
  error?: string;
  paymentMethod: PaymentMethod | null;
}

export function SubscriptionDetails({ 
  userRole, 
  nextBilling, 
  isLoading, 
  error,
  paymentMethod
}: SubscriptionDetailsProps) {
  const getSubscriptionBadge = (subscription: string) => {
    switch (subscription.toLowerCase()) {
      case "enterprise":
        return <Badge className="bg-ai-indigo">Enterprise</Badge>;
      case "pro":
        return <Badge className="bg-premium">Pro</Badge>;
      case "admin":
        return <Badge className="bg-ai-indigo">Admin</Badge>;
      case "basic":
      default:
        return <Badge variant="outline" className="text-gray-500">Basic</Badge>;
    }
  };

  const getPlanName = (role: string) => {
    if (role.toLowerCase() === 'admin') return 'Admin Plan';
    return role.charAt(0).toUpperCase() + role.slice(1) + " Plan";
  };

  const formatExpirationDate = (month?: number, year?: number) => {
    if (!month || !year) return '';
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`;
  };

  const getCardBrandDisplay = (brand: string) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const formatBillingDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return formatDate(new Date(dateString));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Table>
      <TableBody>
        <TableRow>
          <TableCell className="text-sm font-medium text-gray-500">Current Plan</TableCell>
          <TableCell>
            <div className="flex items-center space-x-2">
              <span className="font-medium">
                {getPlanName(userRole || 'basic')}
              </span>
              {getSubscriptionBadge(userRole || 'basic')}
            </div>
          </TableCell>
        </TableRow>
        
        <TableRow>
          <TableCell className="text-sm font-medium text-gray-500">Next Billing Date</TableCell>
          <TableCell className="font-medium">{formatBillingDate(nextBilling)}</TableCell>
        </TableRow>
        
        <TableRow>
          <TableCell className="text-sm font-medium text-gray-500">Payment Method</TableCell>
          <TableCell>
            {paymentMethod ? (
              <div className="flex items-center">
                <CreditCard className="h-5 w-5 text-gray-400 mr-2" />
                <span className="font-medium">
                  {getCardBrandDisplay(paymentMethod.brand)} •••• {paymentMethod.last4}
                  {paymentMethod.expMonth && paymentMethod.expYear && (
                    <span className="text-gray-500 ml-2">
                      Exp: {formatExpirationDate(paymentMethod.expMonth, paymentMethod.expYear)}
                    </span>
                  )}
                </span>
              </div>
            ) : (
              <div className="text-gray-500">No payment method on file</div>
            )}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}
